import CountUp from "react-countup"
import { Flame, PhoneCall, TrendingUp, Moon, AlertTriangle } from "lucide-react"
import { cn } from "../../utils/classnames"
import { LeadScoreCategory, LeadScoreStats } from "../../../types"
import { CATEGORY_CONFIG, CATEGORIES } from "./category-helpers"

interface Props {
  stats: LeadScoreStats | undefined
  activeCategory: LeadScoreCategory | null
  onCategoryClick: (category: LeadScoreCategory | null) => void
}

const ACCENT: Record<
  LeadScoreCategory,
  { bar: string; activeBg: string; activeBorder: string }
> = {
  hot_lead: {
    bar: "bg-[#3f885d]",
    activeBg: "bg-[#f4fff9]",
    activeBorder: "border-2 border-[#3f885d]"
  },
  needs_contact: {
    bar: "bg-[#f3ce49]",
    activeBg: "bg-white",
    activeBorder: "border border-[#e4e4e4]"
  },
  engaged: {
    bar: "bg-[#3392cc]",
    activeBg: "bg-white",
    activeBorder: "border border-[#e4e4e4]"
  },
  dormant: {
    bar: "bg-[#c5c5c5]",
    activeBg: "bg-white",
    activeBorder: "border border-[#e4e4e4]"
  },
  at_risk: {
    bar: "bg-[#c96764]",
    activeBg: "bg-white",
    activeBorder: "border border-[#e4e4e4]"
  }
}

function CategoryIcon({
  category,
  className
}: {
  category: LeadScoreCategory
  className?: string
}) {
  if (category === "hot_lead") {
    return (
      <Flame
        size={16}
        className={cn("shrink-0 text-neutral-500", className)}
        aria-hidden
      />
    )
  }
  const Cmp =
    category === "needs_contact"
      ? PhoneCall
      : category === "engaged"
      ? TrendingUp
      : category === "dormant"
      ? Moon
      : AlertTriangle
  return <Cmp className={cn("h-4 w-4 shrink-0 text-neutral-500", className)} />
}

const ScoreSummaryCards = ({
  stats,
  activeCategory,
  onCategoryClick
}: Props) => {
  const avg =
    stats?.avg_score != null && !Number.isNaN(stats.avg_score)
      ? stats.avg_score.toFixed(1)
      : null

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {CATEGORIES.map((category, i) => {
        const config = CATEGORY_CONFIG[category]
        const accent = ACCENT[category]
        const count = stats?.[category] ?? 0
        const isActive = activeCategory === category
        const isHot = category === "hot_lead"

        return (
          <button
            key={category}
            type="button"
            onClick={() => onCategoryClick(isActive ? null : category)}
            className={cn(
              "relative min-h-[115px] overflow-hidden rounded text-left transition-all",
              "hover:shadow-md",
              isHot && isActive
                ? cn(
                    accent.activeBg,
                    accent.activeBorder,
                    "shadow-md ring-2 ring-[#3f885d]/30"
                  )
                : isActive
                ? cn(
                    "bg-white shadow-md ring-2 ring-violet-400/40",
                    accent.activeBorder
                  )
                : cn(
                    isHot ? accent.activeBg : "bg-white",
                    "border border-[#e4e4e4]"
                  )
            )}
          >
            <div className="relative flex h-full flex-col pl-8 pr-4 py-4">
              <div className="mb-1 flex items-center gap-2 relative">
                <div
                  className={cn(
                    "absolute h-[18px] -left-3 -top-0.5 w-[5px] rounded-full",
                    accent.bar
                  )}
                  aria-hidden
                />
                <CategoryIcon category={category} />
                <span className="text-xs font-normal uppercase tracking-[0.48px]">
                  {config.labelPlural}
                </span>
              </div>
              <div className="text-3xl font-extrabold leading-9">
                <CountUp end={count} duration={0.8} delay={(i + 1) * 0.15} />
              </div>
              {avg != null && (
                <p className="mt-auto text-[13px] leading-[18px] text-[#64748b]">
                  Avg Score: {avg}
                </p>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default ScoreSummaryCards
